/*
 * IMPORTS
 */
import 'mapbox-gl/dist/mapbox-gl.css' // Npm: Mapbox css.
import React from 'react' // Npm: react.js library.
import Geo from 'pangnote-cities' // Npm: Pangnote cities.
import process from 'process' // Node: process library.
import _ from 'underscore' // Npm: underscore.js library.
import Map, { Marker } from 'react-map-gl' // Npm: React mapbox.
import { TbArrowsMaximize, TbArrowsMinimize, TbCircleDotFilled } from 'react-icons/tb' // Npm: React icons.
import { HiArrowPath } from 'react-icons/hi2' // Npm: React icons.
import { useLazyQuery } from '@apollo/client' // Npm: Apollo client.
import {
  Box,
  Button,
  Flex,
  IconButton,
  List,
  ListIcon,
  ListItem,
  Text,
  Tooltip,
  useBreakpointValue,
  useStyleConfig,
  WrapItem
} from '@chakra-ui/react' // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import DateRangePicker from 'components/DateRangePicker'
import TableSpinner from 'components/TableSpinner'

/*
 * GRAPHS
 */
import SmsGroupByQuery from './__query__/index.sms.read.query'


const defaultViewState = {
  latitude: 37.692,
  longitude: -122.435,
  zoom: 0
}

/*
 * OBJECTS
 */
export default function YourTransfers() {
  const mapRef = React.useRef(null)

  // Hook assignment.
  const [dateRange, setDateRange] = React.useState({
    'selection': {
      'startDate': Object.DateRangeProvider()?.startDate,
      'endDate': Object.DateRangeProvider()?.endDate,
      'key': 'selection'
    }
  })
  const [markers, setMarkers] = React.useState([])
  const [refreshTrigger, setRefreshTrigger] = React.useState(0)
  const [isLoading, setIsLoading] = React.useState(false)
  const [QuerySmsGroupBy] = useLazyQuery(SmsGroupByQuery)
  const _styles = useStyleConfig('Card')

  const [isFullScreen, setIsFullScreen] = React.useState(false)
  const [viewState, setViewState] = React.useState(defaultViewState)
  const markerSize = useBreakpointValue({
    base: 15, sm: 15, md: isFullScreen ? 20 : 15, lg: isFullScreen ? 24 : 15, xl: isFullScreen ? 28 : 15,
  })

  // Refresh handler 
  const handleRefresh = React.useCallback(() => {
    setIsLoading(true)
    setRefreshTrigger(prev => prev + 1)
  }, [])

  // Fullscreen toggle handler.
  const toggleFullScreen = React.useCallback(() => {
    setIsFullScreen(prev => {
      const newFullScreen = !prev

      // Reset to default view when exiting fullscreen
      if (!newFullScreen) {
        setViewState(defaultViewState)
      }

      // Resize map after fullscreen toggle
      setTimeout(() => {
        try {
          const map = mapRef.current?.getMap()
          map?.resize?.()
        } catch {
          // Silently handle error
        }
      }, 150)

      return newFullScreen
    })
  }, [])

  // Event handler.
  React.useEffect(() => {
    // Async handler.
    const _Async = async () => {
      try {
        setIsLoading(true)

        /*
         * Fetch mcc with sms sent through
         * the platform.
         */
        const _QuerySmsGroupBy = await QuerySmsGroupBy({
          'variables': {
            'groupBy': ['persisted__countryname'],
            'startDate': dateRange?.selection.startDate,
            'endDate': dateRange?.selection.endDate,
            'take': 1000000,
            'skip': 0
          },
          'fetchPolicy': Object.React.App.fetchPolicy
        })

        // If getting data caught exception then report failure.
        if (_QuerySmsGroupBy instanceof Error) throw _QuerySmsGroupBy

        // If data is found then update markers.
        if (_QuerySmsGroupBy?.data?.SmsGroupBy) {
          // Const assignment.
          const _markers = _.compact(
            Object.entries(_QuerySmsGroupBy?.data?.SmsGroupBy ?? {})?.map?.(j =>
              _.compact([
                Geo.getCountryGeo(j?.[1]?.persisted__countryname),
                j?.[1]?._totalCount,
                j?.[1]?.persisted__countryname
              ])
            )
          )

          // Update markers.
          !_.isEmpty(_markers) && 0 < _markers?.length && setMarkers(_markers?.filter(j => !_.isEmpty(j)))
        }
      } catch (error) {
        console.error('Error fetching SMS data:', error)
      } finally {
        setIsLoading(false)
      }
    }; _Async()
  }, [dateRange, refreshTrigger])

  // Handle viewport changes
  const handleViewStateChange = React.useCallback((evt) => {
    setViewState(evt.viewState)
  }, [])

  // Markers rendering
  const renderedMarkers = React.useMemo(() => {
    return markers.map((p, i) => _.isEmpty(p?.[2]) ? void 0 : (
      <Marker
        key={i}
        latitude={p?.[0]?.[0] ?? 23}
        longitude={p?.[0]?.[1] ?? -102}>
        <WrapItem>
          <Tooltip label={`${p?.[2] ?? 'Unknown'} : ${p[1] ?? 0}`} shouldWrapChildren>
            <TbCircleDotFilled
              color={2000 > p[1] ? 'tomato' : 2000 <= p[1] && 10000 >= p[1] ? 'gray' : 'black'}
              size={markerSize}
            />
          </Tooltip>
        </WrapItem>
      </Marker>
    ))
  }, [markers, markerSize])

  // Return component.
  return (
    <Box
      __css={_styles}
      justifyContent='center'
      align='center'
      position={isFullScreen ? 'fixed' : 'relative'}
      top={isFullScreen ? 0 : 'auto'}
      left={isFullScreen ? 0 : 'auto'}
      zIndex={isFullScreen ? 1000 : 'auto'}
      direction='column'
      w='100%'
      h='100%'
      p={{ base: '12px', md: '22px' }}
      boxShadow='14px 17px 40px 4px rgba(112, 144, 176, 0.17)'>
      <Flex justifyContent='flex-start' alignItems='center' w='100%' mb={{ base: '12px', md: '22px' }} gap={{ base: '6px', md: '12px' }}>
        <Text
          color='rgba(43,54,116,1)'
          fontWeight='700'
          fontSize={{ 'base': 'sm', 'md': 'md' }}
          textAlign='left'
          whiteSpace='nowrap'>
          COUNTRY STATS
        </Text>
        <Flex gap={{ base: '6px', md: '12px' }} alignItems='center' w='100%' justifyContent='space-between'>
          <DateRangePicker
            startDate={dateRange?.selection?.startDate}
            endDate={dateRange?.selection?.endDate}
            w={{ base: 'auto', md: '100%' }}
            flex={{ base: 0, md: 1 }}
            fontSize='13px'
            height='max-content'
            setSelectDate={setDateRange}
          />
          <Flex gap={{ base: '4px', md: '8px' }} alignItems='center' flexShrink={0}>
            <IconButton
              display={{ base: 'flex', md: 'none' }}
              aria-label='Refresh'
              icon={<HiArrowPath size='18px' />}
              onClick={handleRefresh}
              variant='ghost'
              color='purple'
              isDisabled={isLoading}
              _hover={{ bg: 'purple.100' }}
            />
            <Button
              display={{ base: 'none', md: 'flex' }}
              borderRadius='8px'
              fontWeight='450'
              size='sm'
              backgroundColor='purple.200'
              color='purple'
              _hover={{ bg: 'purple.800', color: 'white' }}
              onClick={handleRefresh}
              isDisabled={isLoading}
              leftIcon={<HiArrowPath />}
            >
              Refresh
            </Button>
            <IconButton
              aria-label={isFullScreen ? 'Exit full screen' : 'Enter full screen'}
              icon={isFullScreen ? <TbArrowsMinimize size='18px' /> : <TbArrowsMaximize size='18px' />}
              onClick={toggleFullScreen}
              variant='ghost'
              color='rgba(43,54,116,1)'
              size={{ base: 'sm', md: 'md' }}
              _hover={{ bg: 'gray.100' }}
              _active={{ bg: 'gray.200' }}
            />
          </Flex>
        </Flex>
      </Flex>
      <Flex justify='space-between' h='inherit'>
        <Flex h='100%' w='100%' position='relative' id="map-container">
          {isLoading && <TableSpinner isLoading={true} isEmpty={false} />}
          <Map
            ref={mapRef}
            {...viewState}
            onMove={handleViewStateChange}
            style={{
              'width': '100%',
              'borderRadius': isFullScreen ? '0' : '18px',
              'minHeight': isFullScreen ? '87vh' : '300px',
              'maxHeight': isFullScreen ? '80vh' : '70vh',
              'height': '100%'
            }}
            renderWorldCopies={false}
            mapStyle='mapbox://styles/mapbox/outdoors-v12'
            mapboxAccessToken={process.env.REACT_APP_MAP_KEY}
            reuseMaps
            onLoad={(e) => {
              try {
                const map = e?.target
                if (map && typeof map.setFog === 'function') {
                  map.setFog(null)
                }
              } catch {
                // Silently handle error
              }
            }}
          >
            {renderedMarkers}
          </Map>
          <Flex
            w='170px'
            flexDir='column'
            h='123px'
            borderRadius='15px'
            bg='white'
            bottom='5px'
            left='5px'
            zIndex='2'
            p='12px'
            position='absolute'
            alignItems='start'
            justifyContent='space-between'
            color='#707EAE'>
            <Text fontSize='15px' fontWeight='700'>
              Global SMS Volume
            </Text>
            <List textAlign='left'>
              <ListItem>
                <ListIcon as={TbCircleDotFilled} color='tomato' />
                {`<${2000}`}
              </ListItem>
              <ListItem>
                <ListIcon as={TbCircleDotFilled} color='gray' />
                {`>=${2000}`}
              </ListItem>
              <ListItem>
                <ListIcon as={TbCircleDotFilled} color='black' />
                {`>${10000}`}
              </ListItem>
            </List>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  )
}
